/**
 * Signaling-only message types and guardrails.
 *
 * This module defines the strict boundary between WebRTC connection setup
 * (signaling) and gameplay transport (RTCDataChannel).
 *
 * The signaling channel may only carry connection setup messages. Gameplay
 * messages such as moves, switches, battle state, chat, etc. must never be
 * sent over the signaling WebSocket.
 */

/** Allowed signaling message types. */
export type SignalingMessage =
  | { type: "join"; room: string; role?: "host" | "joiner" }
  | { type: "joined"; room: string; peerId: string }
  | { type: "peer-joined"; room: string; peerId: string }
  | { type: "peer-left"; room: string; peerId: string }
  | { type: "ready"; room: string }
  | { type: "offer"; room: string; sdp: string; candidates: RTCIceCandidateInit[] }
  | { type: "answer"; room: string; sdp: string; candidates: RTCIceCandidateInit[] }
  | { type: "ice-candidate"; room: string; candidate: RTCIceCandidateInit }
  | { type: "leave"; room: string }
  | { type: "error"; room: string; reason: string }
  | { type: "ping"; room: string }
  | { type: "pong"; room: string }
  | { type: "room-full"; room: string }
  | { type: "room-not-found"; room: string };

/** Forbidden gameplay message types that must never be sent over signaling. */
export const FORBIDDEN_SIGNALING_TYPES = new Set([
  "battle-start",
  "battle-action",
  "choose-move",
  "choose-switch",
  "turn-command",
  "turn-result",
  "damage-result",
  "rng-seed",
  "state-sync",
  "state-patch",
  "game-state",
  "chat-message",
  "HELLO",
  "TEAM",
  "BATTLE_CONFIG",
  "BATTLE_START",
  "GUEST_TEAM",
  "ACTION",
  "REMATCH_REQUEST",
  "REMATCH_ACCEPT",
  "REMATCH_DECLINE",
  "SYNC_STATE",
  "BATTLE_STATE",
  "PING",
  "PONG",
  "CHAT",
]);

/**
 * Determine if a candidate is a relay candidate.
 * Only relay candidates are rejected to preserve strict P2P gameplay.
 * Host, srflx, prflx, and private/VPN/mDNS candidates are all accepted.
 */
export function isRelayCandidate(candidate: RTCIceCandidateInit): boolean {
  const type = (candidate as { type?: string }).type;
  if (type === "relay") return true;
  if (candidate.candidate) {
    const sdp = candidate.candidate.toLowerCase();
    if (sdp.includes("typ relay")) return true;
  }
  return false;
}

/** Classify a candidate for logging without leaking full SDP in normal mode. */
export function classifyCandidate(candidate: RTCIceCandidateInit): string {
  const type = (candidate as { type?: string }).type ?? "unknown";
  const relay = isRelayCandidate(candidate);
  return `type=${type}, relay=${relay}`;
}

/**
 * Throw if the message is a gameplay message type.
 * This is a runtime guard against accidentally sending gameplay over signaling.
 */
export function assertSignalingOnly(msg: { type: string }): void {
  if (FORBIDDEN_SIGNALING_TYPES.has(msg.type)) {
    throw new Error(`Blocked gameplay message on signaling channel: ${msg.type}. Gameplay must use RTCDataChannel.`);
  }
}